import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToggle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import {
  addOutline,
  closeCircleOutline,
  createOutline,
  searchOutline,
  trashOutline,
  handLeftOutline,
} from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";

import {
  EntregaItem,
  EntregadorItem,
  FuncionarioActivo,
  ReceptorItem,
  StockMovimiento,
  StockResumen,
  StockSaldoItem,
  Tema,
  actualizarConfig,
  actualizarEntregadores,
  actualizarReceptores,
  actualizarTema,
  anularEntrega,
  buscarFuncionarios,
  cancelarTransferencia,
  cargarIngresoStock,
  crearTema,
  obtenerTransferenciasPendientesTema,
  TransferenciaPendiente,
  entregarManual,
  obtenerConfig,
  obtenerEntregadores,
  obtenerEntregas,
  obtenerMovimientosStock,
  obtenerReceptores,
  obtenerStockResumen,
  obtenerStockSaldos,
  obtenerTemas,
} from "src/services/entregasMasivas";
import puntoDeServicio from "src/services/puntoDeServicio";

interface PuntoServicioItem {
  id: number;
  ps_nombre: string;
  ps_codigo: string;
}

type Tab = "entregadores" | "receptores" | "entregas" | "stock";

const stockTipoLabel: Record<StockMovimiento["tipo"], string> = {
  ingreso: "Ingreso",
  transferencia: "Transferencia",
  entrega: "Entrega",
  reverso_entrega: "Reverso (anulada)",
  consumo: "Consumo / instalación",
  reverso_consumo: "Reverso de consumo",
};

const cardStyle: React.CSSProperties = {
  padding: 12,
  background: "#f4f4f5",
  borderRadius: 12,
  textAlign: "center",
};
const kpiLabelStyle: React.CSSProperties = { fontSize: 12, color: "#666" };
const kpiValueStyle: React.CSSProperties = { fontSize: 28, fontWeight: 700, color: "#222" };

const AdminEntregasMasivas: React.FC = () => {
  const [tab, setTab] = useState<Tab>("entregadores");

  // Temas y seleccion
  const [temas, setTemas] = useState<Tema[]>([]);
  const [temaId, setTemaId] = useState<number | null>(null);
  const [loadingTemas, setLoadingTemas] = useState(true);

  // Entregadores
  const [entregadores, setEntregadores] = useState<EntregadorItem[]>([]);
  const [loadingEnt, setLoadingEnt] = useState(false);
  const [savingEnt, setSavingEnt] = useState(false);

  // Receptores
  const [receptores, setReceptores] = useState<ReceptorItem[]>([]);
  const [loadingRec, setLoadingRec] = useState(false);
  const [savingRec, setSavingRec] = useState(false);
  const [incluirTodos, setIncluirTodos] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Manual: cedula seleccionada para registrar entrega manual
  const [manualCedula, setManualCedula] = useState<string | null>(null);
  const [manualNombre, setManualNombre] = useState<string>("");
  const [registrandoManual, setRegistrandoManual] = useState(false);

  // Busqueda compartida
  const [busqueda, setBusqueda] = useState("");
  const [resultadosBusqueda, setResultadosBusqueda] = useState<FuncionarioActivo[]>([]);
  const [buscando, setBuscando] = useState(false);

  // Entregas
  const [entregas, setEntregas] = useState<EntregaItem[]>([]);
  const [loadingEntregas, setLoadingEntregas] = useState(false);

  const [anularId, setAnularId] = useState<number | null>(null);
  const [present] = useIonToast();

  // Stock
  const [stockResumen, setStockResumen] = useState<StockResumen | null>(null);
  const [stockSaldos, setStockSaldos] = useState<StockSaldoItem[]>([]);
  const [stockMovs, setStockMovs] = useState<StockMovimiento[]>([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [filtroSaldo, setFiltroSaldo] = useState("");
  const [ingresoCantidad, setIngresoCantidad] = useState<string>("");
  const [ingresoMotivo, setIngresoMotivo] = useState<string>("");
  const [ingresoCedula, setIngresoCedula] = useState<string>("");
  const [ingresoCategoria, setIngresoCategoria] = useState<string>("");
  const [ingresoIdentificador, setIngresoIdentificador] = useState<string>("");
  const [ingresoPs, setIngresoPs] = useState<PuntoServicioItem | null>(null);
  const [ingresoPsBusqueda, setIngresoPsBusqueda] = useState<string>("");
  const [ingresoPsResultados, setIngresoPsResultados] = useState<PuntoServicioItem[]>([]);
  const [ingresoPsBuscando, setIngresoPsBuscando] = useState(false);
  const [guardandoIngreso, setGuardandoIngreso] = useState(false);
  const [verHistorialDe, setVerHistorialDe] = useState<string | null>(null);
  const [pendientesTema, setPendientesTema] = useState<TransferenciaPendiente[]>([]);
  const [cancelandoTransf, setCancelandoTransf] = useState<number | null>(null);

  // Formulario de tema (crear / editar)
  const [temaFormOpen, setTemaFormOpen] = useState(false);
  const [temaEditId, setTemaEditId] = useState<number | null>(null);
  const [tfNombre, setTfNombre] = useState("");
  const [tfDescripcion, setTfDescripcion] = useState("");
  const [tfInicio, setTfInicio] = useState("");
  const [tfFin, setTfFin] = useState("");
  const [tfActivo, setTfActivo] = useState(true);
  const [tfControla, setTfControla] = useState(false);
  const [tfModo, setTfModo] = useState<"entrega" | "consumo">("entrega");
  const [tfEtiqueta, setTfEtiqueta] = useState("");
  const [savingTema, setSavingTema] = useState(false);

  const cedulasEntregadores = useMemo(
    () => new Set(entregadores.map((e) => e.cedula)),
    [entregadores]
  );
  const cedulasReceptores = useMemo(
    () => new Set(receptores.map((e) => e.cedula)),
    [receptores]
  );

  const temaActual = useMemo(
    () => temas.find((t) => t.id === temaId) || null,
    [temas, temaId]
  );

  const cargarTemas = async () => {
    setLoadingTemas(true);
    try {
      const data = await obtenerTemas();
      setTemas(data);
      // Selecciona por defecto el primero vigente, o sino el mas reciente.
      const ahora = Date.now();
      const vigente = data.find((t) => {
        const ini = t.fecha_inicio ? new Date(t.fecha_inicio).getTime() : 0;
        const fin = t.fecha_fin ? new Date(t.fecha_fin).getTime() : 0;
        return ini <= ahora && ahora <= fin;
      });
      const elegido = vigente || data[0] || null;
      setTemaId(elegido ? elegido.id : null);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudieron cargar los temas",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setLoadingTemas(false);
    }
  };

  // Recorta un ISO datetime a 'YYYY-MM-DDTHH:MM' para el input datetime-local.
  const toInputDateTime = (iso: string | null | undefined) =>
    iso ? iso.slice(0, 16) : "";

  const abrirNuevoTema = () => {
    setTemaEditId(null);
    setTfNombre("");
    setTfDescripcion("");
    setTfInicio("");
    setTfFin("");
    setTfActivo(true);
    setTfControla(false);
    setTfModo("entrega");
    setTfEtiqueta("");
    setTemaFormOpen(true);
  };

  const abrirEditarTema = () => {
    if (!temaActual) return;
    setTemaEditId(temaActual.id);
    setTfNombre(temaActual.nombre || "");
    setTfDescripcion(temaActual.descripcion || "");
    setTfInicio(toInputDateTime(temaActual.fecha_inicio));
    setTfFin(toInputDateTime(temaActual.fecha_fin));
    setTfActivo(!!temaActual.activo);
    setTfControla(!!temaActual.controla_categorias);
    setTfModo(temaActual.modo_salida === "consumo" ? "consumo" : "entrega");
    setTfEtiqueta(temaActual.etiqueta_consumo || "");
    setTemaFormOpen(true);
  };

  const guardarTema = async () => {
    const nombre = tfNombre.trim();
    if (!nombre) {
      present({ message: "Indica un nombre", duration: 2500, color: "warning" });
      return;
    }
    if (!tfInicio || !tfFin) {
      present({ message: "Indica las fechas de inicio y fin", duration: 2500, color: "warning" });
      return;
    }
    if (tfFin < tfInicio) {
      present({ message: "La fecha de fin no puede ser anterior a la de inicio", duration: 3000, color: "warning" });
      return;
    }
    setSavingTema(true);
    try {
      const payload = {
        nombre,
        descripcion: tfDescripcion.trim() || null,
        fecha_inicio: tfInicio,
        fecha_fin: tfFin,
        activo: tfActivo,
        controla_categorias: tfControla,
        modo_salida: tfModo,
        etiqueta_consumo: tfModo === "consumo" ? tfEtiqueta.trim() || "Instalación" : null,
      };
      const tema = temaEditId
        ? await actualizarTema(temaEditId, payload)
        : await crearTema(payload);
      present({
        message: temaEditId ? "Tema actualizado" : "Tema creado",
        duration: 2500,
        color: "success",
      });
      setTemaFormOpen(false);
      await cargarTemas();
      if (tema?.id) setTemaId(tema.id);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo guardar el tema",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setSavingTema(false);
    }
  };

  const cargarEntregadores = async () => {
    if (!temaId) return;
    setLoadingEnt(true);
    try {
      const data = await obtenerEntregadores(temaId);
      setEntregadores(data);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudieron cargar los entregadores",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setLoadingEnt(false);
    }
  };

  const cargarReceptores = async () => {
    if (!temaId) return;
    setLoadingRec(true);
    try {
      const data = await obtenerReceptores(temaId);
      setReceptores(data);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudieron cargar los receptores",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setLoadingRec(false);
    }
  };

  const cargarConfig = async () => {
    if (!temaId) return;
    setLoadingConfig(true);
    try {
      const data = await obtenerConfig(temaId);
      setIncluirTodos(!!data.incluir_todos);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo cargar la configuracion",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setLoadingConfig(false);
    }
  };

  const cargarEntregas = async () => {
    if (!temaId) return;
    setLoadingEntregas(true);
    try {
      const data = await obtenerEntregas(500, temaId);
      setEntregas(data);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudieron cargar las entregas",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setLoadingEntregas(false);
    }
  };

  useEffect(() => {
    cargarTemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarStock = async () => {
    if (!temaId) return;
    setLoadingStock(true);
    try {
      const [r, s, m, p] = await Promise.all([
        obtenerStockResumen(temaId),
        obtenerStockSaldos(temaId),
        obtenerMovimientosStock({ tema_id: temaId, limite: 200 }),
        obtenerTransferenciasPendientesTema(temaId).catch(() => []),
      ]);
      setStockResumen(r.resumen || null);
      setStockSaldos(s.datos || []);
      setStockMovs(m.datos || []);
      setPendientesTema(p || []);
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo cargar la informacion de stock",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setLoadingStock(false);
    }
  };

  const cancelarPendienteAdmin = async (t: TransferenciaPendiente) => {
    setCancelandoTransf(t.id);
    try {
      await cancelarTransferencia(t.id);
      present({ message: "Transferencia cancelada", duration: 2500, color: "success" });
      await cargarStock();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo cancelar",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setCancelandoTransf(null);
    }
  };

  const buscarPsIngreso = async () => {
    const q = ingresoPsBusqueda.trim();
    if (!q) {
      setIngresoPsResultados([]);
      return;
    }
    setIngresoPsBuscando(true);
    try {
      const resp = await puntoDeServicio.listar(q);
      setIngresoPsResultados((resp?.data?.datos || []).slice(0, 30));
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "Error al buscar puntos de servicio",
        duration: 3000,
        color: "danger",
      });
    } finally {
      setIngresoPsBuscando(false);
    }
  };

  const guardarIngreso = async () => {
    if (!temaId) return;
    const cantidad = parseInt(ingresoCantidad, 10);
    if (!cantidad || cantidad <= 0) {
      present({ message: "Cantidad invalida", duration: 2500, color: "warning" });
      return;
    }
    const categoria = ingresoCategoria.trim();
    if (temaActual?.controla_categorias && !categoria) {
      present({ message: "Este tema requiere indicar la categoria/item", duration: 3000, color: "warning" });
      return;
    }
    const identificador = ingresoIdentificador.trim();
    if (temaActual?.modo_salida === "consumo" && !identificador) {
      present({ message: "El numero de serie / identificador es obligatorio en este tema", duration: 3000, color: "warning" });
      return;
    }
    if (identificador && cantidad !== 1) {
      present({ message: "Un ingreso con numero de serie debe tener cantidad 1", duration: 3000, color: "warning" });
      return;
    }
    if (temaActual?.modo_salida === "consumo" && !ingresoPs) {
      present({ message: "Indica el punto de servicio del item", duration: 3000, color: "warning" });
      return;
    }
    setGuardandoIngreso(true);
    try {
      const cedula = (ingresoCedula || "").trim() || undefined;
      await cargarIngresoStock({
        tema_id: temaId,
        cantidad,
        cedula_destino: cedula,
        motivo: ingresoMotivo.trim() || undefined,
        categoria: categoria || undefined,
        identificador: identificador || undefined,
        punto_servicio: ingresoPs?.ps_codigo,
        punto_servicio_nombre: ingresoPs?.ps_nombre,
      });
      present({
        message: `Ingreso de ${cantidad} ${categoria || "kits"} registrado`,
        duration: 2500,
        color: "success",
      });
      setIngresoCantidad("");
      setIngresoMotivo("");
      setIngresoCedula("");
      // Reponemos la categoria por defecto (etiqueta del tema) para el siguiente
      // ingreso; el usuario puede editarla en cada caso.
      setIngresoCategoria(
        temaActual?.modo_salida === "consumo" ? temaActual?.etiqueta_consumo || "" : ""
      );
      setIngresoIdentificador("");
      setIngresoPs(null);
      setIngresoPsBusqueda("");
      setIngresoPsResultados([]);
      await cargarStock();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo registrar el ingreso",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setGuardandoIngreso(false);
    }
  };

  useEffect(() => {
    if (!temaId) return;
    setBusqueda("");
    setResultadosBusqueda([]);
    if (tab === "entregadores") {
      cargarEntregadores();
    } else if (tab === "receptores") {
      cargarReceptores();
      cargarConfig();
    } else if (tab === "entregas") {
      cargarEntregas();
    } else if (tab === "stock") {
      cargarStock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, temaId]);

  // Precarga la categoria/item del ingreso con la etiqueta configurada del tema
  // (editable). Se aplica al cambiar de tema o de etiqueta, no mientras se tipea.
  useEffect(() => {
    setIngresoCategoria(
      temaActual?.modo_salida === "consumo" ? temaActual?.etiqueta_consumo || "" : ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temaId, temaActual?.modo_salida, temaActual?.etiqueta_consumo]);

  const buscar = async () => {
    const q = busqueda.trim();
    if (!q) {
      setResultadosBusqueda([]);
      return;
    }
    setBuscando(true);
    try {
      const data = await buscarFuncionarios(q);
      setResultadosBusqueda(data.slice(0, 30));
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "Error al buscar funcionarios",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setBuscando(false);
    }
  };

  const guardarEntregadores = async (cedulas: string[]) => {
    if (!temaId) return;
    setSavingEnt(true);
    try {
      const data = await actualizarEntregadores(cedulas, temaId);
      setEntregadores(data);
      present({ message: "Entregadores actualizados", duration: 2500, color: "success" });
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo actualizar la lista",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setSavingEnt(false);
    }
  };

  const guardarReceptores = async (cedulas: string[]) => {
    if (!temaId) return;
    setSavingRec(true);
    try {
      const data = await actualizarReceptores(cedulas, temaId);
      setReceptores(data);
      present({ message: "Receptores actualizados", duration: 2500, color: "success" });
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo actualizar la lista",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setSavingRec(false);
    }
  };

  const cambiarIncluirTodos = async (valor: boolean) => {
    if (!temaId || valor === incluirTodos) return;
    setSavingConfig(true);
    try {
      const data = await actualizarConfig(valor, temaId);
      setIncluirTodos(!!data.incluir_todos);
      present({
        message: valor
          ? "Habilitado para todos los colaboradores"
          : "Restringido a la lista de receptores",
        duration: 2500,
        color: "success",
      });
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo guardar la configuracion",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const agregarEntregador = (f: FuncionarioActivo) => {
    if (cedulasEntregadores.has(f.cedula)) return;
    const nuevas = [...entregadores.map((e) => e.cedula), f.cedula];
    guardarEntregadores(nuevas);
  };

  const quitarEntregador = (cedula: string) => {
    const nuevas = entregadores.filter((e) => e.cedula !== cedula).map((e) => e.cedula);
    guardarEntregadores(nuevas);
  };

  const agregarReceptor = (f: FuncionarioActivo) => {
    if (cedulasReceptores.has(f.cedula)) return;
    const nuevas = [...receptores.map((e) => e.cedula), f.cedula];
    guardarReceptores(nuevas);
  };

  const quitarReceptor = (cedula: string) => {
    const nuevas = receptores.filter((e) => e.cedula !== cedula).map((e) => e.cedula);
    guardarReceptores(nuevas);
  };

  const abrirManual = (f: FuncionarioActivo | EntregadorItem) => {
    setManualCedula(f.cedula);
    setManualNombre(f.nombre || f.cedula);
  };

  const obtenerUbicacion = (): Promise<{
    lat: number | null;
    lng: number | null;
    precision: number | null;
  }> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ lat: null, lng: null, precision: null });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            precision: pos.coords.accuracy ?? null,
          }),
        () => resolve({ lat: null, lng: null, precision: null }),
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    });
  };

  const confirmarManual = async (motivo: string) => {
    if (!manualCedula || !temaId) return;
    setRegistrandoManual(true);
    try {
      const ubicacion = await obtenerUbicacion();
      const resp = await entregarManual({
        cedula: manualCedula,
        motivo,
        lat: ubicacion.lat,
        lng: ubicacion.lng,
        precision: ubicacion.precision,
        tema_id: temaId,
      });
      present({
        message: resp.mensaje || "Entrega manual registrada",
        duration: 3000,
        color: "success",
      });
      // Si estamos en la pestana de entregas, refrescar.
      if (tab === "entregas") cargarEntregas();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo registrar la entrega manual",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setRegistrandoManual(false);
      setManualCedula(null);
      setManualNombre("");
    }
  };

  const anular = async (motivo: string) => {
    if (!anularId) return;
    try {
      await anularEntrega(anularId, motivo);
      present({ message: "Entrega anulada", duration: 2500, color: "success" });
      cargarEntregas();
    } catch (e: any) {
      present({
        message: e?.response?.data?.error || "No se pudo anular",
        duration: 3500,
        color: "danger",
      });
    } finally {
      setAnularId(null);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <IonTitle>Admin Entregas Masivas</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <div style={{ padding: "8px 16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <div style={{ fontSize: 12, color: "#888" }}>Tema</div>
              <div style={{ display: "flex", gap: 6 }}>
                {temaActual && (
                  <IonButton size="small" fill="clear" onClick={abrirEditarTema}>
                    <IonIcon slot="start" icon={createOutline} />
                    Editar
                  </IonButton>
                )}
                <IonButton size="small" onClick={abrirNuevoTema}>
                  <IonIcon slot="start" icon={addOutline} />
                  Nuevo tema
                </IonButton>
              </div>
            </div>
            {loadingTemas && <IonSpinner name="dots" />}
            {!loadingTemas && temas.length === 0 && (
              <div style={{ color: "#777" }}>
                Sin temas. Crea uno con "Nuevo tema".
              </div>
            )}
            {!loadingTemas && temas.length > 0 && (
              <IonSelect
                value={temaId}
                placeholder="Elegi un tema"
                interface="action-sheet"
                onIonChange={(e) => setTemaId(e.detail.value)}
                style={{
                  border: "1px solid #ccc",
                  borderRadius: 8,
                  paddingLeft: 12,
                  "--placeholder-color": "#777",
                }}
              >
                {temas.map((t) => (
                  <IonSelectOption key={t.id} value={t.id}>
                    {t.nombre}
                  </IonSelectOption>
                ))}
              </IonSelect>
            )}
          </div>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)}>
            <IonSegmentButton value="entregadores">
              <IonLabel>Entregadores</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="receptores">
              <IonLabel>Receptores</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="entregas">
              <IonLabel>Entregas</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="stock">
              <IonLabel>Stock</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {!temaId && !loadingTemas && (
          <p style={{ color: "#777" }}>
            No hay temas cargados todavia. Usa el boton "Nuevo tema" para crear
            uno (por ejemplo "Canasta de Navidad" o "Biometricos"). Luego podras
            administrar entregadores, receptores, stock y entregas.
          </p>
        )}

        {temaActual?.controla_categorias && (
          <IonChip color="primary" style={{ marginBottom: 8 }}>
            <IonLabel>Controla categorias / items</IonLabel>
          </IonChip>
        )}

        {temaActual && temaActual.descripcion && (
          <p style={{ color: "#444", marginTop: 0 }}>
            {temaActual.descripcion}
          </p>
        )}
        {temaActual && (temaActual.fecha_inicio || temaActual.fecha_fin) && (
          <p style={{ color: "#777", fontSize: 13, marginTop: -6 }}>
            Vigencia:{" "}
            {temaActual.fecha_inicio
              ? new Date(temaActual.fecha_inicio).toLocaleDateString()
              : "?"}{" "}
            -{" "}
            {temaActual.fecha_fin
              ? new Date(temaActual.fecha_fin).toLocaleDateString()
              : "?"}
          </p>
        )}

        {temaId && tab === "entregadores" && (
          <>
            <h3>Entregadores actuales</h3>
            {loadingEnt && <IonSpinner name="crescent" />}
            {!loadingEnt && entregadores.length === 0 && (
              <p style={{ color: "#777" }}>Aun no hay entregadores configurados.</p>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {entregadores.map((e) => (
                <IonChip key={e.cedula} color={e.activo_en_dwh ? "primary" : "medium"}>
                  <IonLabel>
                    {e.nombre || e.cedula}{" "}
                    <span style={{ color: "#666", fontSize: 12 }}>({e.cedula})</span>
                  </IonLabel>
                  <IonIcon
                    icon={closeCircleOutline}
                    onClick={() => !savingEnt && quitarEntregador(e.cedula)}
                    style={{ cursor: "pointer" }}
                  />
                </IonChip>
              ))}
            </div>

            <h3>Agregar entregador</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <IonInput
                placeholder="Buscar por cedula o nombre"
                value={busqueda}
                onIonChange={(e) => setBusqueda(e.detail.value || "")}
                onKeyUp={(e) => {
                  if ((e as any).key === "Enter") buscar();
                }}
                style={{ border: "1px solid #ccc", borderRadius: 4, paddingLeft: 8 }}
              />
              <IonButton onClick={buscar} disabled={buscando}>
                <IonIcon slot="icon-only" icon={searchOutline} />
              </IonButton>
            </div>

            {buscando && <IonSpinner name="dots" />}
            <IonList>
              {resultadosBusqueda.map((f) => {
                const yaEsta = cedulasEntregadores.has(f.cedula);
                return (
                  <IonItem key={f.cedula}>
                    <IonLabel>
                      <h4>{f.nombre}</h4>
                      <p>
                        {f.cedula} - {f.cargo}
                      </p>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      onClick={() => agregarEntregador(f)}
                      disabled={yaEsta || savingEnt}
                    >
                      {yaEsta ? "Ya esta" : "Agregar"}
                    </IonButton>
                  </IonItem>
                );
              })}
            </IonList>
          </>
        )}

        {temaId && tab === "receptores" && (
          <>
            <IonItem lines="none" style={{ marginBottom: 8 }}>
              <IonLabel className="ion-text-wrap">
                <h3 style={{ margin: 0 }}>Incluir a todos los colaboradores</h3>
                <p style={{ color: "#666" }}>
                  Si esta apagado, solo las cedulas de la lista podran recibir
                  su {temaActual?.descripcion || "presente"}.
                </p>
              </IonLabel>
              <IonToggle
                slot="end"
                checked={incluirTodos}
                disabled={loadingConfig || savingConfig}
                onIonChange={(e) => cambiarIncluirTodos(e.detail.checked)}
              />
            </IonItem>

            <h3>Receptores autorizados</h3>
            {loadingRec && <IonSpinner name="crescent" />}
            {!loadingRec && receptores.length === 0 && (
              <p style={{ color: "#777" }}>
                {incluirTodos
                  ? "Lista vacia. Como 'incluir todos' esta activo, igual todos pueden recibir."
                  : "Aun no hay receptores autorizados. Nadie podra recibir hasta agregar al menos uno o activar 'incluir todos'."}
              </p>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {receptores.map((e) => (
                <IonChip key={e.cedula} color={e.activo_en_dwh ? "primary" : "medium"}>
                  <IonLabel>
                    {e.nombre || e.cedula}{" "}
                    <span style={{ color: "#666", fontSize: 12 }}>({e.cedula})</span>
                  </IonLabel>
                  {temaActual?.modo_salida !== "consumo" && (
                    <IonIcon
                      icon={handLeftOutline}
                      onClick={() => abrirManual(e)}
                      title="Registrar entrega manual"
                      style={{ cursor: "pointer", color: "#1565c0" }}
                    />
                  )}
                  <IonIcon
                    icon={closeCircleOutline}
                    onClick={() => !savingRec && quitarReceptor(e.cedula)}
                    style={{ cursor: "pointer" }}
                  />
                </IonChip>
              ))}
            </div>

            <h3>
              {temaActual?.modo_salida === "consumo"
                ? "Agregar receptor"
                : "Agregar receptor / Registrar entrega manual"}
            </h3>
            <p style={{ color: "#666", marginTop: -6 }}>
              {temaActual?.modo_salida === "consumo"
                ? "Buscar al colaborador y agregarlo a la lista de receptores."
                : "Buscar al colaborador y elegir entre agregarlo a la lista o registrar una entrega manual (cuando el QR no funciona)."}
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <IonInput
                placeholder="Buscar por cedula o nombre"
                value={busqueda}
                onIonChange={(e) => setBusqueda(e.detail.value || "")}
                onKeyUp={(e) => {
                  if ((e as any).key === "Enter") buscar();
                }}
                style={{ border: "1px solid #ccc", borderRadius: 4, paddingLeft: 8 }}
              />
              <IonButton onClick={buscar} disabled={buscando}>
                <IonIcon slot="icon-only" icon={searchOutline} />
              </IonButton>
            </div>

            {buscando && <IonSpinner name="dots" />}
            <IonList>
              {resultadosBusqueda.map((f) => {
                const yaEsta = cedulasReceptores.has(f.cedula);
                return (
                  <IonItem key={f.cedula}>
                    <IonLabel>
                      <h4>{f.nombre}</h4>
                      <p>
                        {f.cedula} - {f.cargo}
                      </p>
                    </IonLabel>
                    {temaActual?.modo_salida !== "consumo" && (
                      <IonButton
                        slot="end"
                        fill="outline"
                        color="primary"
                        onClick={() => abrirManual(f)}
                        disabled={registrandoManual}
                      >
                        Entrega manual
                      </IonButton>
                    )}
                    <IonButton
                      slot="end"
                      onClick={() => agregarReceptor(f)}
                      disabled={yaEsta || savingRec}
                    >
                      {yaEsta ? "Ya esta" : "Agregar"}
                    </IonButton>
                  </IonItem>
                );
              })}
            </IonList>
          </>
        )}

        {temaId && tab === "entregas" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>Historial de entregas</h3>
              <IonButton size="small" fill="outline" onClick={cargarEntregas}>
                Refrescar
              </IonButton>
            </div>
            {loadingEntregas && <IonSpinner name="crescent" />}
            {!loadingEntregas && entregas.length === 0 && (
              <p style={{ color: "#777" }}>Sin entregas registradas.</p>
            )}
            <IonList>
              {entregas.map((e) => (
                <IonItem
                  key={e.id}
                  color={e.anulada ? "light" : undefined}
                  lines="full"
                >
                  <IonLabel className="ion-text-wrap">
                    <h4 style={{ textDecoration: e.anulada ? "line-through" : undefined }}>
                      {e.receptor_nombre || e.cedula_receptor}{" "}
                      <span style={{ color: "#666", fontSize: 12 }}>
                        ({e.cedula_receptor})
                      </span>
                      {e.metodo === "manual" && (
                        <IonChip color="warning" style={{ marginLeft: 6, height: 20 }}>
                          <IonLabel style={{ fontSize: 11 }}>MANUAL</IonLabel>
                        </IonChip>
                      )}
                    </h4>
                    <p>
                      Entregado por <b>{e.entregador_nombre || e.entregado_por}</b>
                      {" - "}
                      {e.fecha_hora ? new Date(e.fecha_hora).toLocaleString() : ""}
                    </p>
                    {(e.categoria || e.identificador) && (
                      <p style={{ color: "var(--garnet, #910434)", margin: 0 }}>
                        Item: <b>{e.categoria || "-"}</b>
                        {e.identificador ? ` (N° ${e.identificador})` : ""}
                      </p>
                    )}
                    {e.metodo === "manual" && e.motivo_manual && (
                      <p style={{ color: "#8b6f00" }}>
                        Motivo: {e.motivo_manual}
                      </p>
                    )}
                    {e.lat != null && e.lng != null && (
                      <p>
                        <a
                          href={`https://www.google.com/maps?q=${e.lat},${e.lng}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Ver ubicacion
                        </a>
                        {e.precision_metros != null
                          ? ` (~${Math.round(e.precision_metros)}m)`
                          : ""}
                      </p>
                    )}
                    {e.anulada && (
                      <p style={{ color: "#c62828" }}>
                        ANULADA por{" "}
                        {e.anulado_por_nombre || e.anulado_por}
                        {e.anulado_en
                          ? ` el ${new Date(e.anulado_en).toLocaleString()}`
                          : ""}
                        {e.motivo_anulacion ? ` - ${e.motivo_anulacion}` : ""}
                      </p>
                    )}
                  </IonLabel>
                  {!e.anulada && (
                    <IonButton
                      slot="end"
                      color="danger"
                      fill="clear"
                      onClick={() => setAnularId(e.id)}
                    >
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  )}
                </IonItem>
              ))}
            </IonList>
          </>
        )}

        {temaId && tab === "stock" && (
          <>
            {pendientesTema.length > 0 && (
              <div
                style={{
                  border: "1px solid #ffe0b2",
                  background: "#fff8e1",
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 16,
                }}
              >
                <h3 style={{ margin: "0 0 8px" }}>
                  Transferencias pendientes ({pendientesTema.length})
                </h3>
                <p style={{ color: "#666", fontSize: 13, margin: "0 0 8px" }}>
                  Envíos aún no aceptados por el receptor. Podés cancelarlos si
                  hubo un error o hay que redirigirlos.
                </p>
                <IonList>
                  {pendientesTema.map((t) => (
                    <IonItem key={t.id} lines="full">
                      <IonLabel className="ion-text-wrap">
                        <h4 style={{ margin: 0 }}>
                          {t.identificador
                            ? `${t.categoria || "Item"} · N° ${t.identificador}`
                            : `${t.cantidad} × ${t.categoria || "unidad(es)"}`}
                        </h4>
                        <p style={{ margin: 0, color: "#555" }}>
                          <b>{t.nombre_origen || t.cedula_origen}</b>
                          {" -> "}
                          <b>{t.nombre_destino || t.cedula_destino}</b>
                        </p>
                        {t.motivo && (
                          <p style={{ margin: 0, fontSize: 12, color: "#777" }}>{t.motivo}</p>
                        )}
                        <p style={{ margin: 0, fontSize: 12, color: "#999" }}>
                          {t.creado_en ? new Date(t.creado_en).toLocaleString() : ""}
                        </p>
                      </IonLabel>
                      <IonButton
                        slot="end"
                        color="danger"
                        fill="outline"
                        disabled={cancelandoTransf === t.id}
                        onClick={() => cancelarPendienteAdmin(t)}
                      >
                        {cancelandoTransf === t.id ? <IonSpinner name="dots" /> : "Cancelar"}
                      </IonButton>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}

            <h3>Resumen del tema</h3>
            {loadingStock && <IonSpinner name="crescent" />}
            {!loadingStock && stockResumen && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                <div style={cardStyle}>
                  <div style={kpiLabelStyle}>Ingresado</div>
                  <div style={kpiValueStyle}>{stockResumen.ingresado}</div>
                </div>
                {temaActual?.modo_salida === "consumo" ? (
                  <div style={cardStyle}>
                    <div style={kpiLabelStyle}>
                      {temaActual?.etiqueta_consumo || "Consumido"}
                    </div>
                    <div style={kpiValueStyle}>{stockResumen.consumido ?? 0}</div>
                  </div>
                ) : (
                  <div style={cardStyle}>
                    <div style={kpiLabelStyle}>Entregado</div>
                    <div style={kpiValueStyle}>{stockResumen.entregado}</div>
                  </div>
                )}
                <div style={cardStyle}>
                  <div style={kpiLabelStyle}>En circulacion</div>
                  <div style={kpiValueStyle}>{stockResumen.en_circulacion}</div>
                </div>
              </div>
            )}

            <h3>Cargar ingreso del proveedor</h3>
            <p style={{ color: "#666", fontSize: 13, marginTop: -4 }}>
              Suma kits al saldo de un administrador. Si dejas la cedula vacia,
              se cargan a tu propio saldo.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <IonItem>
                <IonLabel position="stacked">Cantidad</IonLabel>
                <IonInput
                  type="number"
                  inputmode="numeric"
                  min="1"
                  value={ingresoCantidad}
                  onIonChange={(e) => setIngresoCantidad(e.detail.value || "")}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Cedula admin destino</IonLabel>
                <IonInput
                  value={ingresoCedula}
                  onIonChange={(e) => setIngresoCedula(e.detail.value || "")}
                  placeholder="(opcional, default: vos)"
                />
              </IonItem>
            </div>
            {(temaActual?.controla_categorias ||
              temaActual?.modo_salida === "consumo") && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <IonItem>
                  <IonLabel position="stacked">
                    Categoria / item{" "}
                    {temaActual?.controla_categorias && (
                      <span style={{ color: "#c62828" }}>*</span>
                    )}
                  </IonLabel>
                  <IonInput
                    value={ingresoCategoria}
                    onIonChange={(e) => setIngresoCategoria(e.detail.value || "")}
                    placeholder="Ej: biometrico"
                  />
                </IonItem>
                <IonItem>
                  <IonLabel position="stacked">
                    N° de serie / identificador{" "}
                    {temaActual?.modo_salida === "consumo" && (
                      <span style={{ color: "#c62828" }}>*</span>
                    )}
                  </IonLabel>
                  <IonInput
                    value={ingresoIdentificador}
                    onIonChange={(e) => setIngresoIdentificador(e.detail.value || "")}
                    placeholder={
                      temaActual?.modo_salida === "consumo"
                        ? "Obligatorio (único por item)"
                        : "(opcional, fuerza cantidad 1)"
                    }
                  />
                </IonItem>
              </div>
            )}
            {temaActual?.modo_salida === "consumo" && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 12, color: "#888", margin: "0 0 4px" }}>
                  Punto de servicio del item <span style={{ color: "#c62828" }}>*</span>
                </div>
                {ingresoPs ? (
                  <IonItem lines="full">
                    <IonLabel className="ion-text-wrap">
                      <h4 style={{ margin: 0 }}>{ingresoPs.ps_nombre}</h4>
                      <p style={{ margin: 0 }}>{ingresoPs.ps_codigo}</p>
                    </IonLabel>
                    <IonButton slot="end" fill="clear" onClick={() => setIngresoPs(null)}>
                      Cambiar
                    </IonButton>
                  </IonItem>
                ) : (
                  <>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <IonInput
                        placeholder="Buscar punto de servicio"
                        value={ingresoPsBusqueda}
                        onIonChange={(e) => setIngresoPsBusqueda(e.detail.value || "")}
                        onKeyUp={(e) => {
                          if ((e as any).key === "Enter") buscarPsIngreso();
                        }}
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: 4,
                          paddingLeft: 8,
                          background: "#fff",
                          "--background": "#fff",
                        }}
                      />
                      <IonButton onClick={buscarPsIngreso} disabled={ingresoPsBuscando}>
                        <IonIcon slot="icon-only" icon={searchOutline} />
                      </IonButton>
                    </div>
                    {ingresoPsBuscando && <IonSpinner name="dots" />}
                    <IonList>
                      {ingresoPsResultados.map((ps) => (
                        <IonItem key={ps.id} button onClick={() => setIngresoPs(ps)}>
                          <IonLabel className="ion-text-wrap">
                            <h4>{ps.ps_nombre}</h4>
                            <p>{ps.ps_codigo}</p>
                          </IonLabel>
                        </IonItem>
                      ))}
                    </IonList>
                  </>
                )}
              </div>
            )}
            <IonItem>
              <IonLabel position="stacked">Motivo / referencia</IonLabel>
              <IonInput
                value={ingresoMotivo}
                onIonChange={(e) => setIngresoMotivo(e.detail.value || "")}
                placeholder="Ej: remito 1234 del proveedor"
              />
            </IonItem>
            <IonButton
              expand="block"
              style={{ marginTop: 12 }}
              onClick={guardarIngreso}
              disabled={guardandoIngreso}
            >
              {guardandoIngreso ? "Guardando..." : "Registrar ingreso"}
            </IonButton>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 24,
              }}
            >
              <h3 style={{ margin: 0 }}>Saldos por persona</h3>
              <IonButton size="small" fill="outline" onClick={cargarStock}>
                Refrescar
              </IonButton>
            </div>
            <IonItem>
              <IonIcon icon={searchOutline} slot="start" />
              <IonInput
                placeholder="Filtrar por nombre o cedula"
                value={filtroSaldo}
                onIonChange={(e) => setFiltroSaldo(e.detail.value || "")}
              />
            </IonItem>
            {!loadingStock && stockSaldos.length === 0 && (
              <p style={{ color: "#777" }}>Aun no hay movimientos de stock.</p>
            )}
            <IonList>
              {stockSaldos
                .filter((s) => {
                  const f = filtroSaldo.trim().toLowerCase();
                  if (!f) return true;
                  return (
                    s.cedula.toLowerCase().includes(f) ||
                    (s.nombre || "").toLowerCase().includes(f)
                  );
                })
                .map((s) => (
                  <IonItem
                    key={s.cedula}
                    button
                    onClick={() =>
                      setVerHistorialDe(verHistorialDe === s.cedula ? null : s.cedula)
                    }
                  >
                    <IonLabel>
                      <h4 style={{ margin: 0 }}>
                        {s.nombre || s.cedula}{" "}
                        <span style={{ color: "#666", fontSize: 12 }}>({s.cedula})</span>
                      </h4>
                      <p style={{ margin: 0, color: "#555" }}>
                        Saldo actual: <b>{s.saldo}</b> kits
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
            </IonList>

            {verHistorialDe && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ marginBottom: 4 }}>
                  Movimientos de{" "}
                  {stockSaldos.find((s) => s.cedula === verHistorialDe)?.nombre ||
                    verHistorialDe}
                </h4>
                {(() => {
                  const filtrados = stockMovs.filter(
                    (m) =>
                      m.cedula_origen === verHistorialDe ||
                      m.cedula_destino === verHistorialDe
                  );
                  if (filtrados.length === 0)
                    return (
                      <p style={{ color: "#777", fontSize: 13 }}>
                        Sin movimientos.
                      </p>
                    );
                  return (
                    <IonList>
                      {filtrados.map((m) => (
                        <IonItem key={m.id} lines="full">
                          <IonLabel className="ion-text-wrap">
                            <p style={{ margin: 0 }}>
                              <b>{stockTipoLabel[m.tipo] || m.tipo}</b>{" "}
                              ({m.cantidad}){" "}
                              {m.cedula_origen === verHistorialDe ? "salida" : "entrada"}
                            </p>
                            <p style={{ margin: 0, color: "#555", fontSize: 13 }}>
                              {m.cedula_origen
                                ? m.nombre_origen || m.cedula_origen
                                : "(deposito)"}{" "}
                              {"->"}{" "}
                              {m.cedula_destino
                                ? m.nombre_destino || m.cedula_destino
                                : m.cedula_receptor
                                ? m.nombre_receptor || m.cedula_receptor
                                : "(colaborador)"}
                            </p>
                            {(m.categoria || m.identificador) && (
                              <p style={{ margin: 0, fontSize: 12, color: "var(--garnet, #910434)" }}>
                                {m.categoria || "-"}
                                {m.identificador ? ` (N° ${m.identificador})` : ""}
                              </p>
                            )}
                            {(m.punto_servicio_nombre || m.punto_servicio) && (
                              <p style={{ margin: 0, fontSize: 12, color: "#00695c" }}>
                                📍 {m.punto_servicio_nombre || m.punto_servicio}
                              </p>
                            )}
                            {m.motivo && (
                              <p style={{ margin: 0, fontSize: 12, color: "#777" }}>
                                {m.motivo}
                              </p>
                            )}
                            <p style={{ margin: 0, fontSize: 12, color: "#999" }}>
                              {m.fecha_hora
                                ? new Date(m.fecha_hora).toLocaleString()
                                : ""}
                            </p>
                          </IonLabel>
                        </IonItem>
                      ))}
                    </IonList>
                  );
                })()}
              </div>
            )}

            <h3 style={{ marginTop: 24 }}>Movimientos recientes</h3>
            {!loadingStock && stockMovs.length === 0 && (
              <p style={{ color: "#777" }}>Sin movimientos.</p>
            )}
            <IonList>
              {stockMovs.slice(0, 50).map((m) => (
                <IonItem key={m.id} lines="full">
                  <IonLabel className="ion-text-wrap">
                    <p style={{ margin: 0 }}>
                      <b>{stockTipoLabel[m.tipo] || m.tipo}</b> ({m.cantidad})
                    </p>
                    <p style={{ margin: 0, color: "#555", fontSize: 13 }}>
                      {m.cedula_origen
                        ? m.nombre_origen || m.cedula_origen
                        : "(deposito)"}{" "}
                      {"->"}{" "}
                      {m.cedula_destino
                        ? m.nombre_destino || m.cedula_destino
                        : m.cedula_receptor
                        ? m.nombre_receptor || m.cedula_receptor
                        : "(colaborador)"}
                    </p>
                    {(m.categoria || m.identificador) && (
                      <p style={{ margin: 0, fontSize: 12, color: "var(--garnet, #910434)" }}>
                        {m.categoria || "-"}
                        {m.identificador ? ` (N° ${m.identificador})` : ""}
                      </p>
                    )}
                    {(m.punto_servicio_nombre || m.punto_servicio) && (
                      <p style={{ margin: 0, fontSize: 12, color: "#00695c" }}>
                        📍 {m.punto_servicio_nombre || m.punto_servicio}
                      </p>
                    )}
                    {m.motivo && (
                      <p style={{ margin: 0, fontSize: 12, color: "#777" }}>
                        {m.motivo}
                      </p>
                    )}
                    <p style={{ margin: 0, fontSize: 12, color: "#999" }}>
                      {m.fecha_hora ? new Date(m.fecha_hora).toLocaleString() : ""}
                    </p>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          </>
        )}

        <IonAlert
          isOpen={anularId != null}
          onDidDismiss={() => setAnularId(null)}
          header="Anular entrega"
          message="Indica el motivo de la anulacion. Quedara registrado quien y cuando la anulo."
          inputs={[
            {
              name: "motivo",
              type: "textarea",
              placeholder: "Motivo (minimo 3 caracteres)",
            },
          ]}
          buttons={[
            { text: "Cancelar", role: "cancel" },
            {
              text: "Anular",
              role: "destructive",
              handler: (data) => {
                const motivo = (data?.motivo || "").trim();
                if (motivo.length < 3) {
                  present({ message: "Motivo muy corto", duration: 2500, color: "warning" });
                  return false;
                }
                anular(motivo);
                return true;
              },
            },
          ]}
        />

        <IonAlert
          isOpen={manualCedula != null}
          onDidDismiss={() => {
            setManualCedula(null);
            setManualNombre("");
          }}
          header="Registrar entrega manual"
          message={`Vas a registrar la entrega para ${manualNombre}. Indica el motivo (por que no se uso el QR).`}
          inputs={[
            {
              name: "motivo",
              type: "textarea",
              placeholder: "Ej: QR no se generaba, sin saldo de datos, etc.",
            },
          ]}
          buttons={[
            { text: "Cancelar", role: "cancel" },
            {
              text: "Confirmar",
              handler: (data) => {
                const motivo = (data?.motivo || "").trim();
                if (motivo.length < 3) {
                  present({
                    message: "Motivo muy corto (minimo 3 caracteres)",
                    duration: 2500,
                    color: "warning",
                  });
                  return false;
                }
                confirmarManual(motivo);
                return true;
              },
            },
          ]}
        />

        <IonModal isOpen={temaFormOpen} onDidDismiss={() => setTemaFormOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setTemaFormOpen(false)}>Cancelar</IonButton>
              </IonButtons>
              <IonTitle>{temaEditId ? "Editar tema" : "Nuevo tema"}</IonTitle>
              <IonButtons slot="end">
                <IonButton strong onClick={guardarTema} disabled={savingTema}>
                  {savingTema ? "..." : "Guardar"}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonItem>
              <IonLabel position="stacked">Nombre *</IonLabel>
              <IonInput
                value={tfNombre}
                onIonChange={(e) => setTfNombre(e.detail.value || "")}
                placeholder="Ej: Canasta de Navidad 2026"
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Descripcion</IonLabel>
              <IonTextarea
                value={tfDescripcion}
                onIonChange={(e) => setTfDescripcion(e.detail.value || "")}
                placeholder="Ej: canasta navideña / biometrico"
                autoGrow
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Inicio de vigencia *</IonLabel>
              <IonInput
                type="datetime-local"
                value={tfInicio}
                onIonChange={(e) => setTfInicio(e.detail.value || "")}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Fin de vigencia *</IonLabel>
              <IonInput
                type="datetime-local"
                value={tfFin}
                onIonChange={(e) => setTfFin(e.detail.value || "")}
              />
            </IonItem>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h3 style={{ margin: 0 }}>Activo</h3>
                <p style={{ color: "#666" }}>
                  Si esta apagado, el tema no aparece para operar aunque este
                  dentro de su vigencia.
                </p>
              </IonLabel>
              <IonToggle
                slot="end"
                checked={tfActivo}
                onIonChange={(e) => setTfActivo(e.detail.checked)}
              />
            </IonItem>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h3 style={{ margin: 0 }}>Controla categorias / items</h3>
                <p style={{ color: "#666" }}>
                  Actívalo para entregas que se rastrean por item (ej:
                  biometrico, con categoria y N° de serie). Déjalo apagado para
                  entregas generales como una canasta.
                </p>
              </IonLabel>
              <IonToggle
                slot="end"
                checked={tfControla}
                onIonChange={(e) => setTfControla(e.detail.checked)}
              />
            </IonItem>

            <div style={{ marginTop: 8 }}>
              <h3 style={{ margin: "0 0 6px" }}>¿Cómo se da de baja el stock?</h3>
              {(
                [
                  {
                    v: "entrega" as const,
                    t: "Entrega a un colaborador",
                    d: "El stock se entrega directamente a una persona (escaneando su QR o de forma manual). También permite transferir en cantidad entre entregadores. Ej: canasta navideña.",
                  },
                  {
                    v: "consumo" as const,
                    t: "Instalación / consumo en un punto de servicio",
                    d: "El stock no se entrega a una persona: se recibe por transferencia y luego se registra su instalación/consumo en un punto de servicio, quedando fuera del stock. Ej: biométrico.",
                  },
                ]
              ).map((op) => {
                const activo = tfModo === op.v;
                return (
                  <div
                    key={op.v}
                    onClick={() => setTfModo(op.v)}
                    style={{
                      cursor: "pointer",
                      border: `2px solid ${activo ? "var(--garnet, #910434)" : "#ddd"}`,
                      background: activo ? "rgba(145,4,52,0.06)" : "#fff",
                      borderRadius: 10,
                      padding: "10px 12px",
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ fontWeight: 700, color: activo ? "var(--garnet, #910434)" : "#333" }}>
                      {activo ? "● " : "○ "}
                      {op.t}
                    </div>
                    <div style={{ fontSize: 13, color: "#666", marginTop: 2 }}>{op.d}</div>
                  </div>
                );
              })}
            </div>

            {tfModo === "consumo" && (
              <IonItem>
                <IonLabel position="stacked">
                  Etiqueta de la acción (cómo se llama el consumo)
                </IonLabel>
                <IonInput
                  value={tfEtiqueta}
                  onIonChange={(e) => setTfEtiqueta(e.detail.value || "")}
                  placeholder="Ej: Instalación"
                />
              </IonItem>
            )}
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default AdminEntregasMasivas;
